import React, { useState } from 'react'
import { Button, Space, Typography, theme, Row, Col, Card, Popover, Flex } from 'antd'
import { CheckOutlined, CloseOutlined, DiffOutlined, HistoryOutlined } from '@ant-design/icons'
import {
    useConfirmSuggestionMutation,
    useSkipRefinementItemMutation,
} from '../../../../../../../store/apis/rubric-refinement.api'
import { useDispatch } from 'react-redux'
import PatternIcon from '../../../../../../../components/common/PatternIcon'
import { approveDiffReview, rejectDiffReview } from '../../../../../../../store/slices/rubric-refinement.slice'
import './styles.scss'
import MarkdownRenderer from '../../../../../../../components/common/markdown-renderer/MarkdownRenderer'
import AiIcon from '../../../../../../../components/common/icons/AiIcon'
import TextDiffViewer from './DiffViewer'
import useRubricContext from '../../../../../../../hooks/useRubricContext'
const { Text, Title } = Typography

const TextDiff = ({ oldText = '', newText = '' }) => {
    return (
        <Space direction='vertical'>
            <Flex justify='space-between' align='center'>
                <Text type='secondary'>
                    <AiIcon /> Enhanced Description
                </Text>
                {oldText && (
                    <Popover
                        content={<MarkdownRenderer content={oldText} style={{ maxWidth: '600px' }} />}
                        title='Old Description'
                        trigger='click'
                        placement='top'
                    >
                        <Button type='text' size='small' icon={<HistoryOutlined />}>
                            View old
                        </Button>
                    </Popover>
                )}
            </Flex>
            <MarkdownRenderer content={newText} data-test='refinement-enhanced-description' />
        </Space>
    )
}

const ApproveButton = ({ isDisabled, isApproved, refinementItemId, themeToken }) => {
    const [confirmSuggestion] = useConfirmSuggestionMutation()
    const dispatch = useDispatch()

    const handleApprove = async () => {
        // Optimistically update the UI
        dispatch(approveDiffReview({ refinementItemId }))

        try {
            // Make the API call after UI update
            await confirmSuggestion({ refinementItemId }).unwrap()
            console.log('Suggestion confirmed successfully')
        } catch (error) {
            console.error('Failed to approve changes:', error)
            // We could add a toast notification here if needed, but keeping the optimistic update
        }
    }

    return (
        <Button
            onClick={handleApprove}
            icon={<CheckOutlined />}
            disabled={isDisabled}
            type='text'
            data-test='refinement-approve-button'
            style={
                isApproved
                    ? {
                          backgroundColor: themeToken.colorSuccessBg,
                          borderColor: themeToken.colorSuccess,
                          color: themeToken.colorSuccess,
                          fontWeight: 500,
                      }
                    : {
                          color: themeToken.colorSuccess,
                      }
            }
        >
            {isApproved ? 'Changes Approved' : 'Approve Changes'}
        </Button>
    )
}

const RejectButton = ({ isDisabled, isRejected, refinementItemId, themeToken }) => {
    const [skipRefinementItem] = useSkipRefinementItemMutation()
    const dispatch = useDispatch()

    const handleReject = async () => {
        // Optimistically update the UI
        dispatch(rejectDiffReview({ refinementItemId }))

        try {
            await skipRefinementItem({ refinementItemId }).unwrap()
        } catch (error) {
            console.error('Failed to reject changes:', error)
        }
    }

    return (
        <Button
            onClick={handleReject}
            danger={!isRejected}
            icon={<CloseOutlined />}
            disabled={isDisabled}
            type='text'
            data-test='refinement-reject-button'
            style={
                isRejected
                    ? {
                          backgroundColor: themeToken.colorErrorBg,
                          borderColor: themeToken.colorError,
                          color: themeToken.colorError,
                          fontWeight: 500,
                      }
                    : {}
            }
        >
            {isRejected ? 'Changes Rejected' : 'Reject Changes'}
        </Button>
    )
}

/**
 * Component that renders a diff review message for a suggestion
 * Now works directly with a refinementItem instead of transformed content
 */
const DiffReviewMessage = ({ refinementItem }) => {
    const { token: themeToken } = theme.useToken()

    const { getCategoryById } = useRubricContext()

    const [isDiffView, setIsDiffView] = useState(true)

    const isDisabled = Boolean(refinementItem?.diffReviewChoice)
    const isApproved = refinementItem?.diffReviewChoice === 'approved'
    const isRejected = refinementItem?.diffReviewChoice === 'rejected'

    // Check if there's a meaningful grading reference change
    const hasGradingReferenceChange = 
        refinementItem?.gradingReferenceChange?.oldGradingReference !== 
        refinementItem?.gradingReferenceChange?.newGradingReference

    // If the refinementItem doesn't have suggestions OR grading reference changes, don't render
    const hasSuggestions = refinementItem?.suggestions && refinementItem.suggestions.length > 0
    
    if (!refinementItem || (!hasSuggestions && !hasGradingReferenceChange)) {
        return null
    }

    const renderDiffView = (oldText, newText, title) => {
        const isDescriptionChanged = oldText !== newText
        return (
            <>
                {isDiffView ? (
                    <TextDiffViewer oldValue={oldText} newValue={newText} showDiff={true} title={title} />
                ) : (
                    <>
                        {isDescriptionChanged ? (
                            <TextDiff oldText={oldText} newText={newText} />
                        ) : (
                            <Text style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{newText}</Text>
                        )}
                    </>
                )}
            </>
        )
    }

    const renderCategoryCard = change => {
        const categoryDetails = getCategoryById(change.id)

        return (
            <Card size='small' style={{ height: '100%' }} data-test='refinement-diff-category-card' data-test-id={change.id}>
                <Space direction='vertical' style={{ width: '100%', height: '100%' }} size='middle'>
                    <Space>
                        <Text strong style={{ fontSize: '16px' }}>
                            <PatternIcon
                                id={change.id}
                                colorId={categoryDetails?.colorId}
                                content={categoryDetails?.grade}
                                suffix='pt'
                                type='pattern'
                                color={categoryDetails?.color}
                                style={{ marginRight: '8px', fontWeight: 'normal' }}
                            />{' '}
                            {categoryDetails?.name || `Category ${change.id}`}
                        </Text>
                    </Space>
                    {renderDiffView(change.oldDescription, change.newDescription)}
                </Space>
            </Card>
        )
    }

    // Create a grid layout based on the number of changes
    const renderCategoryGrid = () => {
        return (
            <Row gutter={16}>
                {refinementItem.suggestions.map((change, index) => (
                    <Col span={isDiffView ? 24 : 12} key={change.id || index} style={{ marginBottom: '16px' }}>
                        {renderCategoryCard(change)}
                    </Col>
                ))}
            </Row>
        )
    }

    return (
        <div className='diff-review-message' data-test='refinement-diff-review'>
            <Space direction='vertical' style={{ width: '100%', gap: '0' }}>
                <Space
                    direction='horizontal'
                    size={4}
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingBottom: '8px',
                    }}
                >
                    <Title level={5} className='diff-review__title' style={{ color: themeToken.colorTextSecondary }} data-test='refinement-diff-review-title'>
                        Based on your choices, I suggest the following {hasSuggestions && hasGradingReferenceChange ? 'enhancements' : hasGradingReferenceChange ? 'grading reference enhancement' : 'category enhancements'}:
                    </Title>
                    {hasSuggestions && (
                        <Text type='secondary'>
                            <Button type='text' icon={<DiffOutlined />} onClick={() => setIsDiffView(!isDiffView)} data-test='refinement-diff-view-toggle'>
                                {isDiffView ? 'View Updated Only' : 'View Difference'}
                            </Button>
                        </Text>
                    )}
                </Space>

                {hasSuggestions && renderCategoryGrid()}

                {/* Grading Reference Section */}
                {hasGradingReferenceChange && (
                    <Card size='small'>
                        <Space direction='vertical' style={{ width: '100%' }} size='middle'>
                            <Space>
                                <Text strong style={{ fontSize: '16px' }}>
                                    Grading Reference
                                </Text>
                            </Space>
                            {renderDiffView(
                                refinementItem.gradingReferenceChange.oldGradingReference || '',
                                refinementItem.gradingReferenceChange.newGradingReference || '',
                                'Enhanced Grading Reference',
                            )}
                        </Space>
                    </Card>
                )}

                <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: '8px' }} size='middle'>
                    <ApproveButton
                        isDisabled={isDisabled}
                        isApproved={isApproved}
                        refinementItemId={refinementItem.id}
                        themeToken={themeToken}
                    />
                    <RejectButton
                        isDisabled={isDisabled}
                        isRejected={isRejected}
                        refinementItemId={refinementItem.id}
                        themeToken={themeToken}
                    />
                </Space>
            </Space>
        </div>
    )
}

export default DiffReviewMessage
